import { Button, Skeleton } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { relativeTime } from "@/src/libs/relativeTime";
import type { ModerationLike } from "@/src/pages/Posts/posts.type";

type PostLikesProps = {
  likes: ModerationLike[];
  total: number;
  page: number;
  pageCount: number;
  isLoading: boolean;
  onPageChange: (_page: number) => void;
};

/**
 * Qui a aimé la publication, du plus récent au plus ancien.
 *
 * L'API publique n'en rend jamais que le nombre — la liste complète des cœurs
 * d'un fil populaire n'a aucun sens dans une application. Ici la question est
 * autre : **un compte qui aime systématiquement les publications d'un même
 * autre compte** est un motif qu'aucun compteur ne montre, et c'est ce qu'on
 * vient chercher sur un dossier de harcèlement.
 */
export function PostLikes({
  likes,
  total,
  page,
  pageCount,
  isLoading,
  onPageChange,
}: PostLikesProps) {
  const t = useTranslations("posts");
  const { locale } = useLocale();

  if (isLoading) {
    return (
      <div aria-hidden className="flex flex-col gap-2">
        {[0, 1, 2].map((row) => (
          <Skeleton className="h-6 w-48 rounded-md" key={row} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {likes.map((like) => {
          const age = relativeTime(like.createdAt, locale, Date.now());

          return (
            <li
              className="flex flex-wrap items-center gap-2 text-xs text-muted"
              // Un compte ne peut aimer qu'une fois — l'index unique
              // `post_likes_unique_idx` le garantit —, l'identifiant du compte
              // est donc une clé stable. Le repli sur la date couvre le compte
              // supprimé entre-temps, que le serveur rend en `null`.
              key={like.account?.id ?? like.createdAt}
            >
              <AccountBadge account={like.account} isLinked />
              {age ? <span>{age}</span> : null}
            </li>
          );
        })}
      </ul>

      {pageCount > 1 ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            isDisabled={page <= 1}
            size="sm"
            variant="ghost"
            onPress={() => onPageChange(page - 1)}
          >
            {t("previous")}
          </Button>

          <span className="text-xs text-muted tabular-nums">
            {t("page-of", { page, pages: pageCount })}
          </span>

          <Button
            isDisabled={page >= pageCount}
            size="sm"
            variant="ghost"
            onPress={() => onPageChange(page + 1)}
          >
            {t("next")}
          </Button>

          <span className="text-xs text-muted">
            {t("likes-total", { count: total })}
          </span>
        </div>
      ) : null}
    </div>
  );
}
